/* eslint-disable lit-a11y/click-events-have-key-events */
import { html, LitElement, nothing } from "lit";
import { customElement, property, query, queryAssignedElements, state } from "lit/decorators.js";
import { localized, msg, str } from "@lit/localize";
import { when } from "lit/directives/when.js";
import type { ElementInternals } from "element-internals-polyfill/dist/element-internals";

import styles from './select-field.scss';
import './text-field';
import { SelectFieldOption } from "./select-field-option";
import { TextField } from "./text-field";
import { supportsPopover } from "../utils/support";


type Option = { text: string, value: string, disabled?: boolean, element?: SelectFieldOption };

/**
 * A select field with full text search inspired by Material Design.
 *
 * @slot - Options for the element
 */
@customElement('select-field')
@localized()
export class SelectField extends LitElement {
    static override shadowRootOptions: ShadowRootInit = {
        ...LitElement.shadowRootOptions,
        delegatesFocus: true,
    };

    static override styles = styles;

    /**
     * The floating Material label.
     */
    @property() label = '';

    /**
     * Conveys additional information below the select field, such as how it should
     * be used.
     */
    @property({attribute: 'supporting-text'}) supportingText = '';

    /**
     * Name of this select field.
     */
    @property() name = '';

    /**
     * Should we use "xl" mode of {@link TextField}?
     */
    @property({type: Boolean, reflect: true}) xl = false;

    /**
     * Indicates whether the select field is disabled.
     */
    @property({type: Boolean, reflect: true}) disabled = false;

    /**
     * Indicates whether selecting a non-empty value from the select field is required.
     */
    @property({type: Boolean, reflect: true}) required = false;

    @queryAssignedElements({selector: 'select-field-option'})
    private optionElements!: Array<SelectFieldOption>;

    @state() private options: Option[] = [];
    @state() private isExpanded = false;
    @state() private selectedItemIndex = -1;
    @state() private highlightedItemIndex = -1;
    @state() private visibleItemIndices: number[] = [];
    @state() private customValidity = '';
    @state() private textValue = '';

    @query('#selector')
    private textField!: TextField;

    static formAssociated = true;
    private _internals: ElementInternals;

    private static usePopover = supportsPopover();

    constructor() {
        super();
        this._internals = this.attachInternals();
        this.addEventListener('select-field:optionChanged', this._onOptionChanged);
        this.addEventListener('invalid', this._onInvalid);
    }

    override render() {
        return html`
            <slot @slotchange=${this._onSlotChange}></slot>
            <text-field id="selector" tabindex=0 type="text" .xl=${this.xl}
                .label=${this.label} .supportingText=${this.supportingText}
                .required=${this.required} .disabled=${this.disabled}
                .value=${this.textValue}
                role="combobox"
                autocomplete="off"
                aria-controls="results-list" aria-label=${this.label} aria-expanded=${this.isExpanded}
                aria-autocomplete="list"
                aria-activedescendant="${this.highlightedItemIndex != -1 ? `option-${this.highlightedItemIndex}` : nothing}"
                @blur=${this._onBlur} @input=${this._onInput} @keydown=${this._onKeydown}
                @click=${this._onClick}
                ></text-field>
            <button .disabled=${this.disabled} @click=${this._onClick} aria-hidden="true" tabindex="-1" id="expand-button">
                <usos-icon icon-name="keyboard_arrow_down"></usos-icon>
            </button>
            <div id="results" ?popover=${SelectField.usePopover} ?data-expanded=${this.isExpanded} @mousedown=${this._preventUnfocus} aria-live="polite">
                <ul id="results-list" role="listbox"
                    aria-expanded=${this.isExpanded} aria-label=${msg(str`Pasujące opcje`)}
                >
                    ${this.options.map(this._renderOption.bind(this))}
                </ul>
                ${when(this.visibleItemIndices.length == 0, () => html`<p id="no-matches">${msg(str`Brak dopasowań`)}</p>`)}
            </div>
        `;
    }

    @query('#results[popover]') private popoverElement?: HTMLDivElement;

    private _renderOption(option: Option, index: number) {
        const isSelected = this.selectedItemIndex == index;
        const isHighlighted = this.highlightedItemIndex == index;
        const hidden = !this.visibleItemIndices.includes(index);
        // eslint-disable-next-line lit-a11y/mouse-events-have-key-events
        return html`
            <li id="option-${index}" role="option"
                ?hidden=${hidden}
                ?data-selected=${isSelected}
                ?data-highlighted=${isHighlighted}
                ?data-disabled=${option.disabled}
                aria-selected=${isSelected}
                aria-disabled=${option.disabled ?? 'false'}
                data-index=${index}
                @mouseover=${this._onMouseOver}
                @click=${this._onItemSelect}
            >
                ${when(isSelected, () => html`<usos-icon icon-name="done"></usos-icon>`)}
                <span>${option.text}</span>
            </li>
        `;
    }

    // section: getters and setters

    /**
     * Set list of available options. Note that this list will override a list created from HTML children options.
     *
     * @param options options to include in this select field
     */
    setOptions(options: Option[]) {
        this._syncOptions(options);
    }

    /** Get current input value. */
    get value(): string {
        return this.selectedItemIndex != -1 ? this.options[this.selectedItemIndex].value : '';
    }

    set value(value: string) {
        this.selectedItemIndex = this.options.findIndex(option => option.value == value);
        this._updateOnItemSelected(false);
    }

    // section: event handlers

    private _setExpanded(expanded: boolean) {
        this.isExpanded = expanded;
        if (SelectField.usePopover) {
            if (expanded) {
                this.popoverElement?.showPopover();
                this._setPopoverPosition();
                this._lockScroll();
            } else {
                this.popoverElement?.hidePopover();
                this._unlockScroll();
            }
        }
    }

    private _onBlur() {
        this._setExpanded(false);
        this._updateOnItemSelected(false);
    }

    private _onInput() {
        this.textValue = this.textField.value;
        const newFilter = this.textField.value.toLowerCase();
        this._updateVisibleIndices(newFilter);
        if (!this.visibleItemIndices.includes(this.highlightedItemIndex)) {
            this.highlightedItemIndex = -1;
        }
    }

    private _onClick() {
        this._setExpanded(true);
        this.textField.focus();
    }

    private _onKeydown(e: KeyboardEvent) {
        if (e.key == 'Enter') {
            const changed = this.selectedItemIndex != this.highlightedItemIndex;
            this.selectedItemIndex = this.highlightedItemIndex;
            this._setExpanded(false);
            this._updateOnItemSelected(changed);
        } else if (e.key == 'Escape') {
            this._setExpanded(false);
        } else if (e.key == 'ArrowDown') {
            e.preventDefault(); // disallow moving cursor on arrow up and down
            if (!this.isExpanded) {
                this._setExpanded(true);
                this.highlightedItemIndex = this.visibleItemIndices.length > 0 ? this.visibleItemIndices[0] : -1;
            }
            this.highlightedItemIndex = this.visibleItemIndices.find(idx => idx > this.highlightedItemIndex && !this.options[idx].disabled) ?? this.highlightedItemIndex;
        } else if (e.key == 'ArrowUp') {
            e.preventDefault(); // disallow moving cursor on arrow up and down
            this.highlightedItemIndex = Array.from(this.visibleItemIndices).reverse().find(idx => idx < this.highlightedItemIndex && !this.options[idx].disabled) ?? this.highlightedItemIndex;
        } else {
            const isVisibleKey = !/Arrow|Page|Esc|Enter|Shift|Meta|Alt|Control|Tab|Home|End/.test(e.key);
            if (isVisibleKey) this._setExpanded(true);
        }
    }

    private _preventUnfocus(e: Event) {
        e.preventDefault();
    }

    private _onItemSelect(e: Event) {
        const index = Number((e.currentTarget as HTMLElement).dataset.index);
        if (0 <= index && index < this.options.length && !this.options[index].disabled) {
            const changed = this.selectedItemIndex != index;
            this.selectedItemIndex = index;
            this._setExpanded(false);
            this._updateOnItemSelected(changed);
        }
    }

    private _onMouseOver(e: Event) {
        const index = Number((e.currentTarget as HTMLElement).dataset.index);
        if (0 <= index && index < this.options.length && !this.options[index].disabled)
            this.highlightedItemIndex = index;
    }


    private _onOptionChanged(e: HTMLElementEventMap['select-field:optionChanged']) {
        const itemIndex = this.options.findIndex(option => option.element == e.target);
        if (itemIndex == -1) return;
        const item = this.options[itemIndex];
        item.text = e.detail.text;
        item.value = e.detail.value;
        item.disabled = e.detail.disabled;
        if (this.selectedItemIndex == itemIndex) {
            this.textValue = item.text;
            this._internals.setFormValue(item.value);
        }
    }

    private _onSlotChange() {
        const newOptions: Option[] = this.optionElements.map(el => {
            return { text: el.text, value: el.value, disabled: el.disabled, element: el };
        });
        this._syncOptions(newOptions);
        this.selectedItemIndex = this.optionElements.findIndex(el => el.selected);
        this._updateOnItemSelected(true);
    }

    /**
     * When a form is submitted, invalid events are fired at each form control that is invalid.
     * The validity of submittable elements is checked before submitting their owner <form>,
     * or after the checkValidity() method of the element or its owner <form> is called.
     */
    private _onInvalid() {
        console.log('onInvalid');
        this._syncValidity();
    }

    // section: state update triggers

    private _syncOptions(newOptions: Option[]) {
        const oldSelectedOptionValue = this.selectedItemIndex != -1 ? this.options[this.selectedItemIndex].value : null;
        const newSelectedOptionIndex = newOptions.findIndex(option => option.value == oldSelectedOptionValue);
        this.selectedItemIndex = newSelectedOptionIndex;
        this.options = newOptions;
        this._updateVisibleIndices(this.textValue.toLowerCase());
    }

    private _updateVisibleIndices(filter: string) {
        this.visibleItemIndices = this.options
            .map((option, index) => index == this.selectedItemIndex || option.text.toLowerCase().includes(filter) ? index : null)
            .filter(v => v !== null);
    }

    private _updateOnItemSelected(isChanged: boolean) {
        const selectedOption = this.selectedItemIndex != -1 ? this.options[this.selectedItemIndex] : null;
        this.textValue = selectedOption?.text ?? '';
        this._updateVisibleIndices('');
        // don't use this.value here to diffentiate no selected option from selected empty option
        this._internals.setFormValue(selectedOption?.value ?? null);
        this._syncValidity();
        if (isChanged) {
            this.dispatchEvent(new Event('change'));
        }
    }

    private _syncValidity() {
        const valueMissing = this.required && this.value == '';
        const customError = this.customValidity != '';
        const validationMessage = valueMissing ? msg(str`Należy wybrać opcję.`) : this.customValidity;
        this._internals.setValidity({valueMissing, customError}, validationMessage, this.textField.validityAnchor ?? this.textField);
        this.textField.setCustomValidity(validationMessage);
    }

    // section: validity

    /**
     * Returns whether an element will successfully validate based on forms validation rules and constraints.
     *
     * [MDN Reference](https://developer.mozilla.org/docs/Web/API/HTMLInputElement/willValidate)
     */
    get willValidate() {
        return this._internals.willValidate;
    }

    get validity() {
        return this._internals.validity;
    }

    /**
     * Returns whether a form will validate when it is submitted, without having to submit it.
     *
     * [MDN Reference](https://developer.mozilla.org/docs/Web/API/HTMLInputElement/checkValidity)
     */
    checkValidity() {
        return this._internals.checkValidity();
    }

    /** [MDN Reference](https://developer.mozilla.org/docs/Web/API/HTMLInputElement/reportValidity) */
    reportValidity() {
        const validity = this._internals.reportValidity();
        this._syncValidity();
        return validity;
    }

    /**
     * Sets a custom error message that is displayed when a form is submitted.
     * @param error Sets a custom error message that is displayed when a form is submitted.
     *
     * [MDN Reference](https://developer.mozilla.org/docs/Web/API/HTMLInputElement/setCustomValidity)
     */
    setCustomValidity(error: string) {
        this.textField?.setCustomValidity(error);
        this.customValidity = error;
        this._syncValidity();
    }

    // section popover

    /** Sets popover position */
    private _setPopoverPosition() {
        if (!this.popoverElement) return;

        const doc = document.documentElement,
            scroll = {
                top: (window.pageYOffset || doc.scrollTop)  - (doc.clientTop || 0),
                left: (window.pageXOffset || doc.scrollLeft) - (doc.clientLeft || 0)
            },
            selectBounding = this.getBoundingClientRect();

        this.popoverElement.style.marginTop = `${selectBounding.top + scroll.top}px`;
        this.popoverElement.style.marginLeft = `${selectBounding.left + scroll.left}px`;

        this.popoverElement.style.minWidth = `${selectBounding.width}px`;
        // recalculating width because of paddings
        this.popoverElement.style.minWidth =
            `${selectBounding.width + (selectBounding.width - this.popoverElement.getBoundingClientRect().width)}px`;

        // maxHeight to the bottom of the screen without a few pixels
        const maxHeight = window.innerHeight - this.popoverElement.getBoundingClientRect().top - 13;
        this.popoverElement.style.maxHeight = `${maxHeight}px`;
    }

    /**
     * Locks scroll on closest listening container.
     */
    private _lockScroll() {
        this.dispatchEvent(new CustomEvent('usos-ui:lockScroll', {
            bubbles: true,
            detail: { single: true }
        }));
    }

    /**
     * Unlocks scroll on closest listening container.
     */
    private _unlockScroll() {
        this.dispatchEvent(new CustomEvent('usos-ui:unlockScroll', {
            bubbles: true,
            detail: { single: true }
        }));
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "select-field": SelectField,
    }
    interface HTMLElementEventMap {
        'select-field:optionChanged': CustomEvent<{text: string, value: string, disabled: boolean, selected: boolean}>,
    }
}
